import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRICING_TIERS } from "@/content/pricing";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA } from "@/lib/tracking";

type Props = {
  heading?: string;
  eyebrow?: string;
  subheading?: string;
  className?: string;
};

export const PricingCards = ({
  heading,
  eyebrow = "Choose Your Pathway",
  subheading,
  className,
}: Props) => (
  <section className={cn("py-20 md:py-28", className)}>
    <div className="container">
      {(heading || subheading) && (
        <Reveal variant="up" className="max-w-3xl mb-14 md:mb-20">
          <span className="eyebrow">{eyebrow}</span>
          {heading && (
            <h2 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05]">
              {heading}
            </h2>
          )}
          {subheading && (
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              {subheading}
            </p>
          )}
        </Reveal>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
        {PRICING_TIERS.map((tier, i) => {
          const featured = tier.featured;
          return (
            <Reveal key={tier.id} variant="up" delay={i * 80}>
              <div
                className={cn(
                  "relative h-full flex flex-col border bg-navy/80 backdrop-blur-sm p-6 md:p-7 transition-all duration-300",
                  "hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_hsl(var(--gold)/0.35)]",
                  featured
                    ? "border-gold/60 bg-gradient-to-br from-gold/[0.08] via-navy to-navy shadow-[0_20px_60px_-25px_hsl(var(--gold)/0.4)]"
                    : "border-border hover:border-gold/40",
                )}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-orange text-navy-deep font-display uppercase tracking-[0.2em] text-[10px] px-3 py-1.5 shadow-cta">
                    <Sparkles className="h-3 w-3" />
                    {tier.badge}
                  </div>
                )}

                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  {tier.eyebrow}
                </div>
                <h3 className="font-display uppercase text-white text-xl md:text-2xl mt-3 leading-tight">
                  {tier.name}
                </h3>

                <div className="mt-5 flex items-baseline gap-2">
                  <span
                    className={cn(
                      "font-display text-3xl md:text-4xl",
                      featured ? "text-gradient-gold" : "text-white",
                    )}
                  >
                    {tier.price}
                  </span>
                  {tier.priceCadence && (
                    <span className="text-[11px] uppercase tracking-[0.2em] text-silver/80">
                      {tier.priceCadence}
                    </span>
                  )}
                </div>

                <p className="text-silver text-sm mt-4 leading-relaxed">
                  {tier.description}
                </p>

                <ul className="mt-6 space-y-3 flex-1">
                  {tier.benefits.map((b) => (
                    <li
                      key={b}
                      className="flex gap-3 text-white/85 text-sm leading-relaxed"
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full",
                          featured ? "bg-gold/20 text-gold" : "bg-orange/10 text-orange",
                        )}
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>

                {tier.socialProof && (
                  <div className="mt-6 pt-5 border-t border-border text-[10px] uppercase tracking-[0.22em] text-silver/70">
                    {tier.socialProof}
                  </div>
                )}

                <Link
                  to={tier.ctaHref}
                  onClick={() => {
                    trackCTA("pricing_cta", {
                      tier_id: tier.id,
                      tier_name: tier.name,
                      tier_price: tier.price,
                      featured: !!featured,
                      surface: "pricing_cards",
                    });
                    // Also fire tier-specific CTA events so masterclass and
                    // strategy-session conversion funnels can be filtered
                    // directly in GA4 / dataLayer without regexing tier_id.
                    if (tier.id === "masterclass") {
                      trackCTA("masterclass_cta_click", {
                        surface: "pricing_cards",
                        tier_price: tier.price,
                      });
                    }
                    if (tier.ctaHref.startsWith("/inquire")) {
                      trackCTA("strategy_session_cta_click", {
                        lane: tier.id,
                        surface: "pricing_cards",
                      });
                    }
                  }}
                  className={cn(
                    "group mt-6 inline-flex items-center justify-center gap-2 font-display uppercase tracking-[0.2em] text-xs py-4 px-5 transition-all w-full",
                    featured
                      ? "bg-cta text-white shadow-cta hover:brightness-110"
                      : "border border-gold/40 text-white hover:bg-gold/10 hover:border-gold",
                  )}
                >
                  {tier.ctaLabel}
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
